/**
 * Amazon's `subject_character` attribute (labeled "Cartoon Character" in
 * Seller Central) turned out NOT to mean "names a real licensed character" --
 * confirmed against Isaac's own live catalog 2026-09-22: real values include
 * "Friendly Animals", "Nature" and "Certified Fishing", none of which are
 * actual characters. It's really just a short subject label Amazon wants on
 * nearly every listing, generic ones included.
 *
 * Because the real examples show real human judgment (not a mechanical
 * extraction -- "Certified Master Baiter Funny Bass Fishing..." became
 * "Certified Fishing", not the literal first two words), this is only ever a
 * STARTING SUGGESTION for a human to glance at and adjust before launch, per
 * Isaac's own "derive it but let us review it" rule for this whole class of
 * field. Never treated as authoritative on its own.
 *
 * Kept dependency-free (no drizzle/schema imports) so both the server
 * (`server/routes/channels.ts`, the actual push fallback) and the client
 * (`client/src/pages/Inventory.tsx`, the pre-filled review field) share the
 * exact same guess -- same reasoning as `shared/sku.ts`.
 */

/** Generic e-commerce/product-description words that never describe the
 * actual subject of the design -- filtered out before picking words to
 * suggest. Deliberately broad (covers packaging, materials, surfaces,
 * connective words) since Ninjamo's titles are keyword-stuffed for SEO, not
 * written to be parsed. */
const SUBJECT_FILLER_WORDS = new Set([
  "a",
  "an",
  "and",
  "for",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to",
  "with",
  "pack",
  "set",
  "piece",
  "pieces",
  "sheet",
  "sheets",
  "kit",
  "personalization",
  "vinyl",
  "decal",
  "decals",
  "sticker",
  "stickers",
  "skin",
  "skins",
  "wrap",
  "wraps",
  "adhesive",
  "ultra",
  "thin",
  "die",
  "cut",
  "ideal",
  "perfect",
  "premium",
  "waterproof",
  "uv",
  "resistant",
  "durable",
  "long-lasting",
  "no-residue",
  "high-quality",
  "new",
  "ready",
  "effect",
  "computer",
  "laptop",
  "laptops",
  "notebook",
  "journal",
  "school",
  "supplies",
  "water",
  "bottle",
  "bottles",
  "flask",
  "wall",
  "window",
  "car",
  "phone",
  "tablet",
  "ipad",
  "case",
  "cover",
  "credit",
  "debit",
  "card",
  "cards",
  "cooler",
  "coolers",
  "anime",
  "peeker",
  "peekers",
]);

/** True for a token that's a size/count/pack-size/model-letter and never a
 * meaningful subject word -- "3.5"", "A", "3", and also "10-Pack" (starts
 * with a digit, unlike the pure-punctuation cases above). */
function isNoiseToken(word: string): boolean {
  return /^[\d."'-]+$/.test(word) || /^\d/.test(word) || word.length <= 1;
}

/** Returns a short (1-3 word) starting suggestion for the subject/character
 * label, or undefined if the title has nothing usable left after filtering
 * (rare -- always meant to be reviewed either way). */
export function suggestSubjectCharacter(title: string): string | undefined {
  const words = title
    .replace(/[()[\]"',]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => !isNoiseToken(w))
    .filter((w) => !SUBJECT_FILLER_WORDS.has(w.toLowerCase()));
  if (words.length === 0) return undefined;
  return words.slice(0, 2).join(" ");
}
